import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { colors } from '@scriptpad/shared';
import { useMutation } from '@tanstack/react-query';

import { setPracticeMode } from '../api/client.js';
import { StepPaper } from './StepPaper.js';
import type { WizardStepProps } from './WizardRouter.js';

export const Step2Practice = ({ guid, data, onAdvance, onPatched }: WizardStepProps) => {
  const mutation = useMutation({
    mutationFn: (mode: 'organisation' | 'sole_practitioner') => setPracticeMode(guid, mode),
    onSuccess: (next) => {
      onPatched(next);
      onAdvance(3);
    },
  });

  return (
    <StepPaper>
      <Stack spacing={3}>
        <Typography variant="h3">How do you currently practice?</Typography>

        <Typography variant="body1" color="text.secondary">
          If you are part of a healthcare provider organisation, you will need to provide a NASH PKI
          certificate to us from PRODA so we can register your organisation. If you are a sole
          practitioner, you may prescribe within an independent or outpatient prescribing context available on the platform.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ pt: 2 }}>
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: data.practiceMode === 'organisation' ? colors.primary : 'divider',
              borderWidth: data.practiceMode === 'organisation' ? 2 : 1,
            }}
          >
            <CardActionArea
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('organisation')}
              sx={{ height: '100%' }}
            >
              <CardContent sx={{ p: 4 }}>
                <BusinessIcon sx={{ fontSize: 40, color: colors.primary, mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  I'm part of a healthcare organisation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You'll upload your organisation's NASH PKI certificate.
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: data.practiceMode === 'sole_practitioner' ? colors.primary : 'divider',
              borderWidth: data.practiceMode === 'sole_practitioner' ? 2 : 1,
            }}
          >
            <CardActionArea
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('sole_practitioner')}
              sx={{ height: '100%' }}
            >
              <CardContent sx={{ p: 4 }}>
                <PersonIcon sx={{ fontSize: 40, color: colors.primary, mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  I'm an independent practitioner
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You'll prescribe within an independent or outpatient prescribing context available on the platform.
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Stack>

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Button onClick={() => onAdvance(1)} variant="text">
            Back
          </Button>
        </Stack>
      </Stack>
    </StepPaper>
  );
};
